import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Shield, Package, CheckCircle, ArrowRight } from "lucide-react";

export default function WarrantyPage() {
  return (
    <div data-testid="warranty-page">
      {/* Hero */}
      <section className="hero-gradient py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Badge className="mb-6 text-xs font-medium bg-[#1d1d1f] text-white border-none px-4 py-1.5 rounded-full">
            Trust & Warranty
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1d1d1f] tracking-tight">
            Simple tiers.
            <br />
            <span className="text-[#86868b]">Real protection.</span>
          </h1>
          <p className="mt-6 text-base text-[#86868b] max-w-lg mx-auto leading-relaxed">
            We don't bury you in specs. Pick the tier that's right for you and buy with confidence.
          </p>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-16 sm:py-24" data-testid="tiers-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Basic */}
            <div className="p-8 rounded-2xl border border-gray-200 bg-white" data-testid="tier-basic">
              <span className="text-xs font-bold text-[#86868b] uppercase tracking-widest">
                Tier 1
              </span>
              <h2 className="mt-3 text-xl font-bold text-[#1d1d1f]">Basic</h2>
              <p className="mt-2 text-sm text-[#86868b] leading-relaxed">
                Everything you need to get started. Your MacBook and a charger — tested, clean, ready to go.
              </p>
              <Separator className="my-6" />
              <h3 className="text-sm font-semibold text-[#1d1d1f] mb-3">What's Included</h3>
              <div className="space-y-3">
                {["MacBook", "Charger"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Package className="w-4 h-4 text-[#86868b]" />
                    <span className="text-sm text-[#1d1d1f]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Extended Warranty */}
            <div className="p-8 rounded-2xl border-2 border-[#1d1d1f] bg-white relative" data-testid="tier-warranty">
              <Badge className="absolute -top-3 left-6 bg-[#1d1d1f] text-white text-xs px-3 py-0.5 rounded-full border-none">
                Recommended
              </Badge>
              <span className="text-xs font-bold text-[#86868b] uppercase tracking-widest">
                Tier 2
              </span>
              <h2 className="mt-3 text-xl font-bold text-[#1d1d1f]">With Extended Warranty</h2>
              <p className="mt-2 text-sm text-[#86868b] leading-relaxed">
                Everything in Basic, plus 90-day warranty coverage for extra peace of mind.
              </p>
              <Separator className="my-6" />
              <h3 className="text-sm font-semibold text-[#1d1d1f] mb-3">What's Included</h3>
              <div className="space-y-3">
                {["MacBook", "Charger", "90-day warranty"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    {item.includes("warranty") ? (
                      <Shield className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Package className="w-4 h-4 text-[#86868b]" />
                    )}
                    <span className="text-sm text-[#1d1d1f]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Promise */}
      <section className="py-16 sm:py-24 bg-[#f5f5f7]" data-testid="promise-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-base sm:text-lg font-semibold text-[#1d1d1f] text-center mb-10">
            Our Promise
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { title: "Professionally Tested", desc: "Every MacBook goes through a full diagnostic check before being listed." },
              { title: "Clean macOS Install", desc: "Fresh operating system, no leftover files or accounts." },
              { title: "Honest Condition Ratings", desc: "We rate honestly — Excellent, Very Good, or Good. No surprises." },
              { title: "Fast Shipping", desc: "Your MacBook ships within 1-2 business days of purchase." },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-5 rounded-xl bg-white" data-testid={`promise-${i}`}>
                <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-[#1d1d1f]">{item.title}</h3>
                  <p className="mt-1 text-xs text-[#86868b] leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-base sm:text-lg font-semibold text-[#1d1d1f]">
            Ready to shop?
          </h2>
          <Button
            asChild
            size="lg"
            className="mt-6 rounded-full px-10 h-12 text-sm font-semibold bg-[#1d1d1f] hover:bg-[#1d1d1f]/90"
            data-testid="warranty-shop-btn"
          >
            <Link to="/shop">
              Browse MacBooks <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
